import React, { Fragment } from 'react';
import styled from 'styled-components';
import { Margin } from 'styled-components-spacing';

import { FormattedMessage } from 'i18n';
import Checkmarks from 'routes/_components/layout/components/footer/components/checkmarks/Checkmarks';
// import RecommendedHotels from 'components/recommendedHotels/RecommendedHotels';
import LinksListSection from '../../components/LinksListSection/LinksListSection';
import Tabs from '../../components/Tabs/Tabs';
import TabMail from './components/tabs/TabMail/TabMailComponent';
import TabPhoneNumber from './components/tabs/TabPhoneNumber/TabPhoneNumber';
import FAQButton from './components/FAQButton/FAQButtonComponent';
import flexbox from 'styles/utils/flexbox';
import { mediaQueries } from 'styles/utils/mediaQueries';
import loadableVisibility from 'react-loadable-visibility/loadable-components';
import InfiniteThreeDots from 'components/spinners/InfiniteThreeDots';

const RecommendedHotels = loadableVisibility(() => import('components/recommendedHotels/RecommendedHotels'), {
  fallback: <InfiniteThreeDots />,
});

function CustomerServiceTemplateComponent({
  selectedSmiley,
  content,
  faq,
  language,
  airlineWhitelabel,
  brandName,
  onClickSmiley,
}) {
  const { title } = content;

  return (
    <Fragment>
      <Background />
      <Root>
        <Title>{title}</Title>
        <Subheader>
          <FormattedMessage
            id={'customerService.faq.label'}
            description={'Frequently Asked Questions'}
            defaultMessage={'Frequently Asked Questions'}
          />
        </Subheader>
        <Margin top='2' />
        <FAQWrap>
          <LinksListSection items={faq} settings={{ rows: 2 }} />
        </FAQWrap>
        <Margin top='2' />
        <FAQButton language={language} airlineWhitelabel={airlineWhitelabel} brandName={brandName} />
        <Margin top='5' />
        <FeedbackContainer>
          <FeedbackQuestion>
            <FormattedMessage
              id={'customerService.feedback.question'}
              description={'Did you get an answer?'}
              defaultMessage={'Did you get an answer?'}
            />
          </FeedbackQuestion>
          <Margin top='2' />
          <SmileyWrap>
            <HappyFace
              role='img'
              aria-label='happy face'
              selected={selectedSmiley === 'happy'}
              data-smiley-id='happy'
              onClick={onClickSmiley}
            >
              &#128515;
            </HappyFace>
            <Margin inline right='2' />
            <SadFace
              role='img'
              aria-label='sad face'
              selected={selectedSmiley === 'sad'}
              data-smiley-id='sad'
              onClick={onClickSmiley}
            >
              &#128532;
            </SadFace>
          </SmileyWrap>
        </FeedbackContainer>
        <Margin top='2' />
        {selectedSmiley === 'happy' && (
          <PositiveFeedbackWrap>
            <FormattedMessage id={'customerService.feedback.positive'} defaultMessage={'Bra!'} />
          </PositiveFeedbackWrap>
        )}
        {selectedSmiley === 'sad' && (
          <ContactWrap>
            <Tabs
              tabs={[
                {
                  id: 'contact-mail',
                  label: {
                    id: 'mail.label',
                    description: 'Mail',
                    defaultMessage: 'Mail',
                  },
                  component: <TabMail key={'contact-mail'} language={language} airlineWhitelabel={airlineWhitelabel} />,
                },
                {
                  id: 'contact-telephone',
                  label: {
                    id: 'telephone.label',
                    description: 'Telephone',
                    defaultMessage: 'Telephone',
                  },
                  component: <TabPhoneNumber key={'contact-telephone'} />,
                },
              ]}
              orientation={'vertical'}
            />
          </ContactWrap>
        )}
      </Root>
      <Margin top='4' />
      <RecommendedHotels />
      <Checkmarks hideHighlights />
    </Fragment>
  );
}

export default CustomerServiceTemplateComponent;

const Background = styled.div`
  position: absolute;
  z-index: -1;
  margin-top: 358px;
  width: 100vw;
  height: 100%;
  background: ${({ theme }) =>
    theme.colors.footer.waves ? theme.colors.footer.waves : 'linear-gradient(to bottom, #fff9f2, #ffffff)'};
  border-top: 1px solid ${({ theme }) => theme.colors.primary.default};

  ${mediaQueries.fromAndBelow.tablet`
    margin-top: 334px;
  `}
`;

const Root = styled.div`
  ${flexbox({
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  })}

  position: relative;
  width: 992px;
  margin: 0 auto;

  ${mediaQueries.fromAndBelow.desktop`
    width: 100%;
    padding: 16px;
  `}
`;

const Title = styled.h1`
  width: 100%;
  margin-top: 130px;
  margin-bottom: 15px;
  font-family: ${({ theme }) => theme.fonts.displaySubtle};
  font-size: 72px;
  font-weight: bold;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.templates.header};
  letter-spacing: 3px;

  ${mediaQueries.fromAndBelow.tablet`
    font-size: 48px;
  `}
`;

const Subheader = styled.h3`
  width: 100%;
  margin-top: 35px;
  margin-bottom: 15px;
  text-align: center;

  font-family: ${({ theme }) => theme.fonts.displaySubtle};
  font-size: 32px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.text.templates.header};
  letter-spacing: 1px;

  ${mediaQueries.fromAndBelow.tablet`
    margin-top: 20px;
    font-size: 24px;
  `}
`;

const FAQWrap = styled.div`
  width: 100%;
`;

const FeedbackContainer = styled.div``;

const FeedbackQuestion = styled.h3`
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.displaySubtle};
  font-size: 32px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.text.templates.header};
  letter-spacing: 1px;
`;

const SmileyWrap = styled.div`
  text-align: center;
`;

const HappyFace = styled.span`
  font-size: 40px;
  cursor: pointer;

  opacity: ${({ selected }) => (selected ? '1' : '0.3')};
`;

const SadFace = styled.span`
  font-size: 40px;
  cursor: pointer;

  opacity: ${({ selected }) => (selected ? '1' : '0.3')};
`;

const PositiveFeedbackWrap = styled.div`
  font-size: 32px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.text.templates.header};
  letter-spacing: 1px;
`;

const ContactWrap = styled.div`
  width: 100%;
`;
